import React, { FC, ReactNode } from 'react';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { StrategyProvider } from '@app/state/mintStore';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import Image from 'next/image';
import { LiquidityWizardMinInfo } from '@app/screens/liquidity/LiquidityWizard/hooks/useDerivedMintInfo';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';

interface Props {
  mintInfo: LiquidityWizardMinInfo;
}

export const ProviderSelector: FC<Props> = ({ mintInfo }) => {
  const {
    actions: { setProvider, setStrategy },
    provider,
    mode
  } = useLiquidityWizardState();

  function renderViewButton(label: ReactNode, _view: StrategyProvider) {
    const isActive = _view.toLowerCase() === provider?.toLowerCase();

    return (
      <Button
        variant="tertiary"
        width="fit-content"
        height="100%"
        px={10}
        onClick={() => {
          setProvider(_view);
          setStrategy(null);
        }}
        sx={{
          fontSize: 14,
          color: isActive ? 'orange' : 'gray.4',
          bg: isActive ? 'gray.2' : 'transparent',
          textTransform: 'capitalize'
        }}
      >
        {label}
      </Button>
    );
  }

  if (mode === 'manual') {
    return null;
  }

  if (!mintInfo.flags.hasStrategies) {
    return (
      <Alert
        status="warning"
        display="flex"
        border="1px solid"
        borderColor="neutral.700"
        mt="16px"
        borderRadius="10px"
        py="10px"
        px="12px"
      >
        <AlertIcon w="16px" h="16px" mr="8px" color="neutral.400" />
        <AlertDescription color="neutral.400">
          There are no auto strategies for selected pool
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack w="100%" gap={4}>
      <HStack
        bg="gray.2"
        borderRadius={4}
        w="100%"
        justifyContent="space-between"
        p={4}
      >
        <Text fontSize={16}>Select a provider</Text>
        <CheckIcon color="success" width={4} height={4} />
      </HStack>
      <HStack
        width="100%"
        sx={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'gray.5',
          padding: '6px'
        }}
      >
        {mintInfo.flags.hasSteerStrategies &&
          renderViewButton(
            <Image
              src="/assets/images/providers/steer.svg"
              height={24}
              width={50}
              alt="steer"
            />,
            'steer'
          )}
        {mintInfo.flags.hasIchiStrategies &&
          renderViewButton(
            <Image
              src="/assets/images/providers/ichi.svg"
              height={24}
              width={50}
              alt="ichi"
            />,
            'ichi'
          )}
        {mintInfo.flags.hasGammaStrategies &&
          renderViewButton(
            <Image
              src="/assets/images/providers/gamma.svg"
              height={24}
              width={50}
              alt="gamma"
            />,
            'gamma'
          )}
      </HStack>
    </VStack>
  );
};
